import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { PropertyTypeFactory } from '@core/property-types/property-type-factory';
import { PropertyType } from '@shared/definitions/property';

/**
 * Property Room Configuration Service
 *
 * Delegates to `PropertyTypeFactory` per property type. Each handler
 * (Apartment, House, Warehouse, Office, Retail, Villa, Land, …) owns the
 * rooms/features it supports; the service just looks the handler up and
 * returns its configuration.
 *
 * This file used to also hold ~6,000 lines of in-service `*Config` getters
 * mirroring each handler's data — all dead since the factory migration.
 * Deleted 2026-04-21.
 */

// ==================== CONFIGURATION INTERFACES ====================

/**
 * Feature category determines where the feature is managed and displayed.
 * - PROPERTY_ONLY: always at property/unit level (e.g. balcony, unit layout)
 * - BUILDING_ONLY: always at building level (e.g. elevator, intercom)
 * - FLEXIBLE: either scope depending on configuration (e.g. heating, parking)
 */
export enum FeatureCategory {
  PROPERTY_ONLY = 'property',
  BUILDING_ONLY = 'building',
  FLEXIBLE = 'flexible'
}

/** Complete configuration for a property type. */
export interface PropertyRoomConfig {
  propertyType: string;
  roomTypes: RoomTypeConfig[];
  availableFeatures: FeatureConfig[];
}

/** Configuration for a specific room type (bedroom, bathroom, office room, …). */
export interface RoomTypeConfig {
  typeKey: string;
  label: string;
  pluralLabel: string;
  /** Field in `PropertyCharacteristics` that stores the count — e.g. `bedrooms`. */
  countField: string;
  /** Material icon name. */
  icon: string;
  formFields: RoomFormField[];
  defaultCount: number;
  description?: string;
}

/** Configuration for a feature (pool, garden, security, …). */
export interface FeatureConfig {
  key: string;
  label: string;
  icon: string;
  category: FeatureCategory;
  /** For `FLEXIBLE` features, specifies the actual scope when resolved. */
  scopeType?: 'property' | 'building';
  formFields: RoomFormField[];
  description?: string;
  /** Name of the interface (for documentation). */
  detailsInterface: string;
}

/**
 * Configuration for an individual form field. `default` is intentionally
 * `unknown` — each field type constrains the concrete value (text → string,
 * number → number, checkbox → boolean) and callers narrow at the binding
 * boundary.
 */
export interface RoomFormField {
  name: string;
  label: string;
  type: 'text' | 'number' | 'checkbox' | 'select' | 'textarea' | 'date';
  placeholder?: string;
  default?: unknown;
  validators?: ValidatorFn[];
  /** For `select` fields. */
  options?: { value: string; label: string }[];
  hint?: string;
  /** Unit suffix — e.g. `m²`, `m`. */
  suffix?: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
}

@Injectable({ providedIn: 'root' })
export class PropertyRoomConfigService {
  constructor(private propertyTypeFactory: PropertyTypeFactory) {}

  /** Resolve the complete configuration for a property type. */
  getConfigForPropertyType(propertyType: string): PropertyRoomConfig {
    const handler = this.propertyTypeFactory.getHandler(propertyType as PropertyType);
    return {
      propertyType: handler.propertyType,
      roomTypes: handler.getRoomTypes(),
      availableFeatures: handler.getFeatures()
    };
  }

  /** Resolve one room type inside a property type, or `null` if it doesn't exist. */
  getRoomTypeConfig(propertyType: string, roomTypeKey: string): RoomTypeConfig | null {
    const handler = this.propertyTypeFactory.getHandler(propertyType as PropertyType);
    return handler.getRoomTypeConfig(roomTypeKey) || null;
  }

  /** Resolve one feature inside a property type, or `null` if it doesn't exist. */
  getFeatureConfig(propertyType: string, featureKey: string): FeatureConfig | null {
    const handler = this.propertyTypeFactory.getHandler(propertyType as PropertyType);
    return handler.getFeatureConfig(featureKey) || null;
  }

  /** List every room type supported by the property type. */
  getRoomTypes(propertyType: string): RoomTypeConfig[] {
    const handler = this.propertyTypeFactory.getHandler(propertyType as PropertyType);
    return handler.getRoomTypes();
  }

  /** List every feature supported by the property type. */
  getAvailableFeatures(propertyType: string): FeatureConfig[] {
    const handler = this.propertyTypeFactory.getHandler(propertyType as PropertyType);
    return handler.getFeatures();
  }

  /** Escape hatch — hand back the raw handler for advanced use cases. */
  getHandler(propertyType: string) {
    return this.propertyTypeFactory.getHandler(propertyType as PropertyType);
  }
}
